/*
	Settings.tsx -- Page that contains tabs for user settings, company settings, and configuration settings for external integrations.
*/

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type JSX, useState } from 'react';
import { generatePath } from 'react-router';

import { AnalyticsApi } from 'Api/Analytics/AnalyticsApi';
import { ControlsApi } from 'Api/Controls/ControlsApi';
import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { RiskRegisterApi } from 'Api/RiskRegister/RiskRegisterApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { TagsApi } from 'Api/Tags/TagsApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACComponent } from 'Components/Context/RBACComponent';
import { Role } from 'Components/Context/RBACContext';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { ICON_FEATURE_AI_GOVERNANCE, ICON_FEATURE_CONTROL_AUTOMATION, ICON_FEATURE_GLOBAL, ICON_FEATURE_REPORTING_AND_EXPORTS, ICON_FEATURE_RISK_MANAGEMENT, ICON_FEATURE_TPRM, ICON_REFRESH, ICON_SETTINGS } from 'Config/Icons';
import { ADVANCED_FEATURES_TOGGLER, CUSTOM_TEXT_CONFIGURATION, EXTERNAL_INTEGRATION_SETTINGS, TOGGLE_CONTROL_SETTINGS, TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING, TPRM_IRQ_SETTINGS, TPRM_TIMEFRAMES_CONFIGURATION, UCRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING, UCRM_IRQ_SETTINGS, UCRM_TIMEFRAMES_CONFIGURATION, USER_SETTINGS } from 'Config/PathTemplates';
import { AdvancedFeature } from 'Models/ClientDetailsModel';
import { ProductModule } from 'Models/ProductModule';

import { ManageFolderTypesModal } from './ManageFolderTypesModal/ManageFolderTypesModal';
import { ManageRiskCategoriesModal } from './ManageRiskCategoriesModal/ManageRiskCategoriesModal';
import { ManageRiskStrategiesModal } from './ManageRiskStrategiesModal/ManageRiskStrategiesModal';
import { ManageTagsModal } from './ManageTagsModal/ManageTagsModal';
import styles from './Settings.module.css';
import { TargetMaturityModal } from './TargetMaturityModal/TargetMaturityModal';

enum PageElements {
    None,
    ManageFolderTypes,
    ManageUcrmFolderTypes,
    ManageRiskCategories,
    ManageRiskStrategies,
    ManageTagsModal,
    ResetDefaultAnalysis,
    TargetMaturity,
}

export interface SettingsProps {
    analyticsApi: AnalyticsApi;
    controlsApi: ControlsApi;
    dashboardApi: DashboardApi;
    riskRegisterApi: RiskRegisterApi;
    tagsApi: TagsApi;
    tprmApi: ClientTPRMApi;
    ucrmApi: ClientUcrmApi;
}

export const Settings = (props: SettingsProps): JSX.Element => {
    const [displayedPageElement, setDisplayedPageElement] = useState<PageElements>(PageElements.None);
    const cachedData = useCachedData();

    const resetAnalysis = async (): Promise<string> => {
        await props.analyticsApi.resetAnalysis();
        return 'Default analysis reset.';
    };

    return (
        <RBACComponent roles={[Role.ADMIN]}>
            {displayedPageElement === PageElements.ManageFolderTypes && <ManageFolderTypesModal hideModal={() => setDisplayedPageElement(PageElements.None)} module={ProductModule.TPRM} moduleApi={props.tprmApi} />}
            {displayedPageElement === PageElements.ManageUcrmFolderTypes && <ManageFolderTypesModal hideModal={() => setDisplayedPageElement(PageElements.None)} module={ProductModule.UCRM} moduleApi={props.ucrmApi} />}
            {displayedPageElement === PageElements.ManageRiskCategories && <ManageRiskCategoriesModal hideModal={() => setDisplayedPageElement(PageElements.None)} riskRegisterApi={props.riskRegisterApi} />}
            {displayedPageElement === PageElements.ManageRiskStrategies && <ManageRiskStrategiesModal hideModal={() => setDisplayedPageElement(PageElements.None)} riskRegisterApi={props.riskRegisterApi} />}
            {displayedPageElement === PageElements.ManageTagsModal && <ManageTagsModal hideModal={() => setDisplayedPageElement(PageElements.None)} tagsApi={props.tagsApi} />}
            {displayedPageElement === PageElements.ResetDefaultAnalysis && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText="Reset Default Analysis" areYouSureText="Are you sure you want to reset the Default Analysis?" buttonText="RESET" buttonLoadingText="Resetting..." buttonIcon={ICON_REFRESH} performOperation={resetAnalysis} hideModal={() => setDisplayedPageElement(PageElements.None)}>
                    <Text>Any changes your organization made to the Default Analysis will be lost. If your organization renamed the Default Analysis it will be recreated. Other analyses your organization created will not be affected.</Text>
                </ConfirmationModal>
            )}
            {displayedPageElement === PageElements.TargetMaturity && <TargetMaturityModal hideModal={() => setDisplayedPageElement(PageElements.None)} dashboardApi={props.dashboardApi} />}
            <PageLayoutDefault
                headerTitle="Settings"
                body={[
                    {
                        content: (
                            <>
                                <div className={styles.icon}>
                                    <FontAwesomeIcon icon={ICON_FEATURE_GLOBAL} />
                                    <Text variant="Header4" noStyles>
                                        Global Settings
                                    </Text>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Advanced Features</Text>
                                        <Text variant="Text3" noStyles>
                                            Enable or disable advanced features in SummIT.
                                        </Text>
                                    </div>
                                    <Link variant="secondaryButton" to={generatePath(ADVANCED_FEATURES_TOGGLER)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Link>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Custom Text</Text>
                                        <Text variant="Text3" noStyles>
                                            Customize explanatory text that members of your organization will see during various workflows.
                                        </Text>
                                    </div>
                                    <Link variant="secondaryButton" to={{ pathname: generatePath(CUSTOM_TEXT_CONFIGURATION) }} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Link>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">External Integrations</Text>
                                        <Text variant="Text3" noStyles>
                                            Configure integrations with external applications and services to automate data collection.
                                        </Text>
                                    </div>
                                    <Link variant="secondaryButton" to={generatePath(EXTERNAL_INTEGRATION_SETTINGS)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Link>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Tags</Text>
                                        <Text variant="Text3" noStyles>
                                            Manage tags to label and organize various entities.
                                        </Text>
                                    </div>
                                    <Button variant="secondary" onClick={() => setDisplayedPageElement(PageElements.ManageTagsModal)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Button>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">User Management</Text>
                                        <Text variant="Text3" noStyles>
                                            Create and modify user accounts. Assign roles to users to control access to SummIT features and data.
                                        </Text>
                                    </div>
                                    <Link variant="secondaryButton" to={generatePath(USER_SETTINGS)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Link>
                                </div>
                                {cachedData.clientHasAdvancedFeature([AdvancedFeature.ANALYTICS]) && (
                                    <>
                                        <hr className={styles.hr} />
                                        <div className={styles.icon}>
                                            <FontAwesomeIcon icon={ICON_FEATURE_REPORTING_AND_EXPORTS} />
                                            <Text variant="Header4" noStyles>
                                                Reporting and Exports
                                            </Text>
                                        </div>
                                        <div className={styles.settingRowContainer}>
                                            <div className={styles.settingDescription}>
                                                <Text variant="Header3">Reset Default Analysis</Text>
                                                <Text noStyles>Reset the Default Analysis using the latest template provided by High Peaks Solutions.</Text>
                                            </div>
                                            <Button variant="secondary" onClick={() => setDisplayedPageElement(PageElements.ResetDefaultAnalysis)} fontAwesomeImage={ICON_REFRESH}>
                                                RESET
                                            </Button>
                                        </div>
                                    </>
                                )}
                                <hr className={styles.hr} />
                                <div className={styles.icon}>
                                    <FontAwesomeIcon icon={ICON_FEATURE_RISK_MANAGEMENT} />
                                    <Text variant="Header4" noStyles>
                                        Risk Management
                                    </Text>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Risk Categories</Text>
                                        <Text noStyles>Define categories that risks are grouped by, such as Financial, Legal, Operational, Reputational, and Technology.</Text>
                                    </div>
                                    <Button variant="secondary" onClick={() => setDisplayedPageElement(PageElements.ManageRiskCategories)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Button>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Risk Strategies</Text>
                                        <Text noStyles>Define strategies for handling risks, such as Accept, Mitigate, and Transfer.</Text>
                                    </div>
                                    <Button variant="secondary" onClick={() => setDisplayedPageElement(PageElements.ManageRiskStrategies)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Button>
                                </div>
                                <hr className={styles.hr} />
                                <div className={styles.icon}>
                                    <FontAwesomeIcon icon={ICON_FEATURE_CONTROL_AUTOMATION} />
                                    <Text variant="Header4" noStyles>
                                        Control Automation
                                    </Text>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Control Selector</Text>
                                        <Text noStyles>Enable and disable controls, groups of controls, or entire control frameworks. Disabled items will not appear within SummIT, will remove related configurations such as limits, notifications, and recurring assessments, and will unmap things like metrics, governance documents, compliance requirements, and open issues/exceptions.</Text>
                                    </div>
                                    <Link variant="secondaryButton" to={generatePath(TOGGLE_CONTROL_SETTINGS)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Link>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">FFIEC CAT Target Maturity</Text>
                                        <Text noStyles>Select the FFIEC CAT maturity level your organization is currently targeting.</Text>
                                    </div>
                                    <Button variant="secondary" onClick={() => setDisplayedPageElement(PageElements.TargetMaturity)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Button>
                                </div>
                                <hr className={styles.hr} />
                                <div className={styles.icon}>
                                    <FontAwesomeIcon icon={ICON_FEATURE_TPRM} />
                                    <Text variant="Header4" noStyles>
                                        Third-Party Risk Management
                                    </Text>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Folder Types</Text>
                                        <Text noStyles>Manage folder types for Third-Party Risk Management document uploads.</Text>
                                    </div>
                                    <Button variant="secondary" onClick={() => setDisplayedPageElement(PageElements.ManageFolderTypes)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Button>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Inherent Risk Questionnaire</Text>
                                        <Text noStyles>Configure the questions and scoring used to calculate the inherent risk of third-party services.</Text>
                                    </div>
                                    <Link variant="secondaryButton" to={generatePath(TPRM_IRQ_SETTINGS)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Link>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Third-Party Due Diligence Assessments</Text>
                                        <Text noStyles>Configure the assessments that can be sent to third-party service contacts when performing due diligence.</Text>
                                    </div>
                                    <Link variant="secondaryButton" to={generatePath(TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Link>
                                </div>
                                <div className={styles.settingRowContainer}>
                                    <div className={styles.settingDescription}>
                                        <Text variant="Header3">Timeframes</Text>
                                        <Text noStyles>Configure the expected durations for third-party users and internal users to complete various stages of the third-party service due diligence workflow.</Text>
                                    </div>
                                    <Link variant="secondaryButton" to={generatePath(TPRM_TIMEFRAMES_CONFIGURATION)} fontAwesomeImage={ICON_SETTINGS}>
                                        EDIT
                                    </Link>
                                </div>
                                {cachedData.clientHasAdvancedFeature([AdvancedFeature.UCRM]) && (
                                    <>
                                        <hr className={styles.hr} />
                                        <div className={styles.icon}>
                                            <FontAwesomeIcon icon={ICON_FEATURE_AI_GOVERNANCE} />
                                            <Text variant="Header4" noStyles>
                                                AI Governance
                                            </Text>
                                        </div>
                                        <div className={styles.settingRowContainer}>
                                            <div className={styles.settingDescription}>
                                                <Text variant="Header3">Folder Types</Text>
                                                <Text noStyles>Manage folder types for AI Governance document uploads.</Text>
                                            </div>
                                            <Button variant="secondary" onClick={() => setDisplayedPageElement(PageElements.ManageUcrmFolderTypes)} fontAwesomeImage={ICON_SETTINGS}>
                                                EDIT
                                            </Button>
                                        </div>
                                        <div className={styles.settingRowContainer}>
                                            <div className={styles.settingDescription}>
                                                <Text variant="Header3">Inherent Risk Questionnaire</Text>
                                                <Text noStyles>Configure the questions and scoring used to calculate the inherent risk of use cases.</Text>
                                            </div>
                                            <Link variant="secondaryButton" to={generatePath(UCRM_IRQ_SETTINGS)} fontAwesomeImage={ICON_SETTINGS}>
                                                EDIT
                                            </Link>
                                        </div>
                                        <div className={styles.settingRowContainer}>
                                            <div className={styles.settingDescription}>
                                                <Text variant="Header3">Use Case Due Diligence Assessments</Text>
                                                <Text noStyles>Configure the assessments that can be sent to use case contacts when performing due diligence.</Text>
                                            </div>
                                            <Link variant="secondaryButton" to={generatePath(UCRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING)} fontAwesomeImage={ICON_SETTINGS}>
                                                EDIT
                                            </Link>
                                        </div>
                                        <div className={styles.settingRowContainer}>
                                            <div className={styles.settingDescription}>
                                                <Text variant="Header3">Timeframes</Text>
                                                <Text noStyles>Configure the expected durations for users to complete various stages of the use case due diligence workflow.</Text>
                                            </div>
                                            <Link variant="secondaryButton" to={generatePath(UCRM_TIMEFRAMES_CONFIGURATION)} fontAwesomeImage={ICON_SETTINGS}>
                                                EDIT
                                            </Link>
                                        </div>
                                    </>
                                )}
                            </>
                        ),
                    },
                ]}
            />
        </RBACComponent>
    );
};
